export interface OutsideTapDetectionStrategy { 
    isOutside(e: Event): boolean;
}

export class ContainmentStategy {
    
    private elements: HTMLElement[];

    constructor(...elements: HTMLElement[]) {
        this.elements = elements;
    }

    isOutside(e) {
        return !this.elements.some(el => el.contains(e.target));
    }
}

export class BoundingBoxStrategy {
        
    constructor(private element: HTMLElement) {}

    isOutside(e) {
        let {left, right, top, bottom} = this.element.getBoundingClientRect();
        let {clientX, clientY} = e;
        if (e.target instanceof HTMLOptionElement) {
            return false; // In Firefox, the option gets a coordinate of (0,0) after it is selected, so we have to ignore it.
        }
        return clientX < left || clientX > right || clientY < top || clientY > bottom;
    }
}

export interface OutsideTapListener {
    onOutsideTap(e: Event): void;
}

/**
 * Dispatches an outside-tap event when the user taps outside of the element.
 * Reasons for using this instead of a click event:
 * - User can cancel the tap by moving the pointer inside the element before releasing it.
 * - Clicks on document.body might not fire a click event if the body does not fill the viewport (need to verify).
 * Note: Initially implemented as a Stimulus controller, but turned out to be problematic when a tap detector was nested inside another tap detector. Example: a dropdown menu nested inside of a dialog or data table. Also, it was not easy to switch between different containment strategies, and required extra code to add and remove event listeners for the outside tap. 
 */
export class OutsideTapDetector implements OutsideTapDetectionStrategy {

    private timeWhenPointerDown: number;
    private threshold = 300;

    constructor(
        private callbacks: OutsideTapListener, 
        private strategy: OutsideTapDetectionStrategy
    ) {}

    enable() {
        document.addEventListener("mousedown", this.onPointerDown);
        document.addEventListener("touchstart", this.onPointerDown, { passive: true });
        document.addEventListener("mouseup", this.onPointerUp);
        document.addEventListener("touchend", this.onPointerUp, { passive: true });
    }

    disable() {
        document.removeEventListener("mousedown", this.onPointerDown);
        document.removeEventListener("touchstart", this.onPointerDown);
        document.removeEventListener("mouseup", this.onPointerUp);
        document.removeEventListener("touchend", this.onPointerUp);
    }

    onPointerDown = (e) => {
        // console.log('pointer down', {outside: this.isOutside(e)});
        if (this.isOutside(e)) {
            this.timeWhenPointerDown = Date.now();
        }
    }

    onPointerUp = (e) => {
        if (this.isOutside(e) && this.isTap()) {
            this.onOutsideTap(e);
        }
    }

    isOutside(e) {
        return this.strategy.isOutside(e);
    }

    isTap() {
        return this.duration < this.threshold;
    }

    get duration() {
        return Date.now() - this.timeWhenPointerDown;
    }

    onOutsideTap(e) {
        this.callbacks.onOutsideTap(e);
    }
}

/** Mimics a tap on the specified element, for testing purposes. */
export function tap(element: HTMLElement, clientX = 0, clientY = 0) {
    let event = {clientX, clientY, bubbles: true};
    element.dispatchEvent(new MouseEvent('mousedown', event));
    element.dispatchEvent(new MouseEvent('mouseup', event));
}